/**
 * The thematic-domain rail — **client review R3**.
 *
 * > *"Move the 8 domain chips out of the Indicators pane into a slim vertical mini-panel
 * > docked LEFT of the main sidebar panel (icon chips, tooltips, All at top); the selected
 * > domain filters BOTH the Indicators pane and the Layers pane."*
 *
 * Two things changed, and only one of them is layout.
 *
 * **The chip grid left the Indicators pane.** It was §F5.2's first block — a two-column grid
 * of nine labelled chips, about 150 px of a 332 px pane — and it filtered a list the reader
 * had to scroll past it to reach. As a rail it is 46 px of width instead, taken from the map
 * rather than from the pane, and it is *beside* the thing it filters rather than above it.
 *
 * **The filter became the sidebar's, not the Indicators pane's.** `domFilter` already lived
 * in the §F1 store; what it did not do was reach the Layers pane, which had its own free-text
 * box and eight domain groups and no way to say "only water". One control now narrows both,
 * which is the client's ask and also the honest model: a domain is a property of the
 * catalogue, not of one tab's view of it.
 *
 * Unchanged, deliberately: **the filter never touches the map** (§F5.2 is explicit, and
 * §F5.11 pins it). A filtered-out indicator stays painted and a filtered-out layer stays
 * drawn — the rail hides rows, not pixels. That is why the selected chip's tooltip says
 * "filter", and why turning the rail to a domain with nothing on in it changes nothing about
 * what is on screen.
 *
 * **The chips are labelled** — client review C2, 2026-08-22: *"each chip in the left domain
 * rail shows the domain short name UNDER its icon"*. They were icon-only, which asked a
 * reader to learn eight glyphs before they could use the one control that filters both panes;
 * a droplet and a sun are guessable, a shield and a cpu are not. `short_name` is the §D1
 * column that exists precisely for this (it is what the indicator rows already print), so the
 * rail is now 68 px of width carrying nine legible words instead of 46 px of pictograms.
 *
 * The full name stays on `title` and `aria-label`: the visible label is deliberately the
 * *short* one — "Governance & GESI" fits, "Governance, Social Inclusion & Institutions" does
 * not — and an abbreviation a screen reader read out instead of the real name would be a
 * worse announcement than the icon was. The rail remains a `role="toolbar"` so the nine chips
 * are announced as one group rather than nine loose buttons.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { fetchConfig, queryKeys, type Domain } from '../../lib/api';
import { Icon, ICONS, type IconName } from '../../lib/icons';
import { useAppStore } from '../../lib/state';

function iconFor(name: string | undefined): IconName {
  return name && name in ICONS ? (name as IconName) : 'globe';
}

interface RailChipProps {
  domain: Domain | null;
  selected: boolean;
  /** Full name — hover text and accessible name. */
  label: string;
  /** C2: the word printed under the icon. `short_name`, or "All" for the top chip. */
  short: string;
  onClick: () => void;
}

function RailChip({ domain, selected, label, short, onClick }: RailChipProps) {
  const isAll = domain === null;
  return (
    <button
      type="button"
      className={`dom-mini${isAll ? ' all' : ''}${selected ? ' sel' : ''}`}
      data-dom={isAll ? '' : domain.id}
      aria-pressed={selected}
      title={label}
      aria-label={label}
      onClick={onClick}
      style={
        selected
          ? {
              borderColor: isAll ? 'var(--brand)' : domain.color,
              background: isAll ? 'var(--brand-tint)' : `${domain.color}14`,
            }
          : undefined
      }
    >
      <span
        className="ci"
        aria-hidden="true"
        style={{ background: isAll ? (selected ? 'var(--brand)' : '#8B958A') : domain.color }}
      >
        <Icon name={isAll ? 'globe' : iconFor(domain.icon)} size={14} />
      </span>
      {/*
        `aria-hidden`, because the button already has the *full* name as its accessible name
        and a screen reader announcing "Water. Water Resources & Hydrology" is reading the
        same control twice. The word is for eyes; the label is for everything else.
      */}
      <span className="dom-mini-label" aria-hidden="true">
        {short}
      </span>
    </button>
  );
}

export function DomainRail() {
  const { t } = useTranslation();
  const domFilter = useAppStore((s) => s.domFilter);
  const setDomFilter = useAppStore((s) => s.setDomFilter);
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const domains = config?.domains ?? [];

  return (
    <div id="domRail" role="toolbar" aria-label={t('domainRail.title')} aria-orientation="vertical">
      <RailChip
        domain={null}
        selected={!domFilter}
        label={t('domainRail.all')}
        short={t('domainRail.allShort')}
        onClick={() => setDomFilter('')}
      />
      <div className="dom-rail-sep" aria-hidden="true" />
      {domains.map((d) => (
        <RailChip
          key={d.id}
          domain={d}
          selected={domFilter === d.id}
          label={d.name}
          // §D1's `short_name` is the column written for exactly this: a label that fits a
          // chip. Falling back to the full name keeps a domain legible if the API ever
          // serves an empty one, rather than printing a blank chip.
          short={d.short_name || d.name}
          // §F5.2's "click-again clears", kept: the rail has no separate "off" and the
          // selected chip is the only control a reader who wants everything back can find.
          onClick={() => setDomFilter(domFilter === d.id ? '' : d.id)}
        />
      ))}
    </div>
  );
}

export default DomainRail;
